import React, { useState } from "react";
import { CATEGORY_LIST } from "../services/CategoryModel";
import { AvatarsL } from "../hooks/useAvatarUtils";
import { useUser } from "../context/user";
import styles from "./Onboarding.module.css";

export default function OnboardingScreen({ onComplete, onBackToLogin }) {
  // Use the user context directly so setUserData is always available
  const { userData = {}, setUserData } = useUser();

  // State: Wizard step navigation (0: Name/Email, 1: BirthDate, 2: Gender, 3: Avatar, 4: Bio, 5: Interests)
  const [step, setStep] = useState(0);
  const totalSteps = 6;

  const handleToggleInterest = (catId) => {
    setUserData((prev = {}) => {
      const currentInterests = prev.interests || [];
      return {
        ...prev,
        interests: currentInterests.includes(catId)
          ? currentInterests.filter((item) => item !== catId)
          : [...currentInterests, catId],
      };
    });
  };

  const handleNextStep = () => {
    if (step === 0 && (!userData.name || !userData.email)) {
      alert("Please enter your name and email");
      return;
    }
    if (step === 1 && !userData.birthDate) {
      alert("Please select your birth date");
      return;
    }
    if (step === 2 && !userData.gender) {
      alert("Please select your gender");
      return;
    }
    if (step === 3 && !userData.avatar) {
      alert("Please choose an avatar");
      return;
    }

    if (step < totalSteps - 1) {
      setStep(step + 1);
    } else {
      onComplete();
    }
  };

  const handlePrevStep = () => {
    if (step > 0) {
      setStep(step - 1);
    } else if (onBackToLogin) {
      onBackToLogin();
    }
  };

  return (
    <div className={styles.container}>
      {/* Top progress bar indicator */}
      <div className={styles.progressBarContainer}>
        {Array.from({ length: totalSteps }).map((_, index) => (
          <div
            key={index}
            className={`${styles.progressBarSegment} ${
              index <= step ? styles.progressBarActive : ""
            }`}
          />
        ))}
      </div>

      {/* STEP 0: Who's there? (Name & Email) */}
      {step === 0 && (
        <div className={styles.stepWrapper}>
          <div className={styles.headerSection}>
            <h1 className={styles.title}>Who&apos;s there?</h1>
            <p className={styles.subtitle}>
              Tell us your name and email so people know what to call you.
            </p>
          </div>

          <div className={styles.formContent}>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>Full Name</label>
              <input
                type="text"
                placeholder="Enter your name..."
                value={userData.name || ""}
                className={styles.inputField}
                onChange={(e) =>
                  setUserData({ ...userData, name: e.target.value })
                }
              />
            </div>

            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>Email Address</label>
              <input
                type="email"
                placeholder="name@example.com"
                value={userData.email || ""}
                className={styles.inputField}
                onChange={(e) =>
                  setUserData({ ...userData, email: e.target.value })
                }
              />
            </div>
          </div>
        </div>
      )}

      {/* STEP 1: Enter your date of birth */}
      {step === 1 && (
        <div className={styles.stepWrapper}>
          <div className={styles.headerSection}>
            <h1 className={styles.title}>Enter your date of birth</h1>
            <p className={styles.subtitle}>
              This helps us recommend people and activities that are a better
              fit for you. Your date of birth won&apos;t be shown to anyone.
            </p>
          </div>

          <div className={styles.formContent}>
            <div className={styles.inputGroup}>
              <input
                type="date"
                value={userData.birthDate || ""}
                className={styles.inputField}
                onChange={(e) =>
                  setUserData({ ...userData, birthDate: e.target.value })
                }
              />
            </div>
          </div>
        </div>
      )}

      {/* STEP 2: What&apos;s your gender? */}
      {step === 2 && (
        <div className={styles.stepWrapper}>
          <div className={styles.headerSection}>
            <h1 className={styles.title}>What&apos;s your gender?</h1>
            <p className={styles.subtitle}>
              We use this information to personalise your recommendations. It
              won&apos;t be visible to other users.
            </p>
          </div>

          <div className={styles.formContent}>
            <div className={styles.radioGroup}>
              {[
                { label: "Male", value: "male" },
                { label: "Female", value: "female" },
                { label: "Non-binary", value: "non-binary" },
                { label: "I prefer not to say", value: "prefer-not-to-say" },
              ].map((item) => (
                <div
                  key={item.value}
                  onClick={() =>
                    setUserData({ ...userData, gender: item.value })
                  }
                  className={`${styles.radioOption} ${
                    userData.gender === item.value
                      ? styles.radioOptionSelected
                      : ""
                  }`}
                >
                  <span>{item.label}</span>
                  <span className={styles.radioIndicator}></span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* STEP 3: Choose your Avatar */}
      {step === 3 && (
        <div className={styles.stepWrapper}>
          <div className={styles.headerSection}>
            <h1 className={styles.title}>Choose your Avatar</h1>
            <p className={styles.subtitle}>
              Select one of your custom illustrations to represent you on
              meetly.
            </p>
          </div>

          <div className={styles.formContent}>
            {/* 2. Update the map reference to use AvatarsL */}
            <div className={styles.avatarGrid}>
              {(AvatarsL || []).map((avatarObj) => {
                const isSelected = userData.avatar === avatarObj.id;
                return (
                  <div
                    key={avatarObj.id}
                    onClick={() =>
                      setUserData({
                        ...userData,
                        avatar: avatarObj.id,
                      })
                    }
                    className={`${styles.avatarItem} ${
                      isSelected
                        ? styles.avatarItemSelected
                        : styles.avatarItemDefault
                    }`}
                  >
                    <div className={styles.avatarImgWrapper}>
                      <img src={avatarObj.src} alt={avatarObj.id} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* STEP 4: Tell us about yourself (Bio) */}
      {step === 4 && (
        <div className={styles.stepWrapper}>
          <div className={styles.headerSection}>
            <h1 className={styles.title}>Your Bio</h1>
            <p className={styles.subtitle}>
              Write a short description about yourself so others can know you
              better when inviting you.
            </p>
          </div>

          <div className={styles.formContent}>
            <div className={styles.inputGroup}>
              <textarea
                placeholder="E.g., Software developer living in Dublin, loves coffee and board games..."
                value={userData.bio || ""}
                rows={4}
                className={styles.inputField}
                style={{ resize: "none", height: "120px", padding: "0.75rem" }}
                onChange={(e) =>
                  setUserData({ ...userData, bio: e.target.value })
                }
              />
            </div>
          </div>
        </div>
      )}

      {/* STEP 5: Your Interests */}
      {step === 5 && (
        <div className={styles.stepWrapper}>
          <div className={styles.headerSection}>
            <h1 className={styles.title}>Your Interests</h1>
            <p className={styles.subtitle}>
              Pick what you like to discover tailored activities around Dublin.
            </p>
          </div>

          <div className={styles.formContent}>
            <div className={styles.interestsContainer}>
              {CATEGORY_LIST.map((cat) => {
                const currentInterests = userData.interests || [];
                const isSelected = currentInterests.includes(cat.id);
                return (
                  <span
                    key={cat.id}
                    onClick={() => handleToggleInterest(cat.id)}
                    className={`${styles.interestTag} ${
                      isSelected
                        ? styles.interestTagSelected
                        : styles.interestTagDefault
                    }`}
                  >
                    <span>{cat.icon}</span>
                    <span>{cat.label}</span>
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Bottom navigation buttons */}
      <div className={styles.footerButtonGroup}>
        <button onClick={handlePrevStep} className={styles.secondaryButton}>
          Back
        </button>
        <button onClick={handleNextStep} className={styles.primaryButton}>
          {step === totalSteps - 1 ? "Complete Setup" : "Next"}
        </button>
      </div>
    </div>
  );
}
